import type { FormAnswers } from '@church/nextjs-ui/website/forms';

/**
 * A HALF-FILLED FORM SURVIVES A WRONG TAP
 * =======================================
 * The Connect Card is seven questions long and gets filled on a phone in a
 * lobby. Backing out of it, following a link in the instructions, or letting
 * the screen sleep should not cost somebody the whole thing.
 *
 * Only the answers are kept — never the name, email or phone. These forms are
 * also filled on shared iPads at a welcome desk, and the next person in line
 * should never find a stranger's contact details waiting in the boxes. The
 * answers alone are not identifying, and they are the part that takes time.
 */

const PREFIX = 'wbc-form-draft-';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Draft {
  at: number;
  answers: FormAnswers;
}

const key = (formId: number) => `${PREFIX}${formId}`;

export function loadDraft(formId: number): FormAnswers | null {
  try {
    const raw = localStorage.getItem(key(formId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as Draft;
    if (!draft?.answers || typeof draft.answers !== 'object') return null;
    if (!draft.at || Date.now() - draft.at > MAX_AGE_MS) {
      localStorage.removeItem(key(formId));
      return null;
    }
    return draft.answers;
  } catch {
    return null;
  }
}

export function saveDraft(formId: number, answers: FormAnswers): void {
  try {
    if (!Object.keys(answers).length) {
      localStorage.removeItem(key(formId));
      return;
    }
    localStorage.setItem(key(formId), JSON.stringify({ at: Date.now(), answers } satisfies Draft));
  } catch {
    /* private mode, or storage full: a draft is a convenience, never a requirement */
  }
}

export function clearDraft(formId: number): void {
  try {
    localStorage.removeItem(key(formId));
  } catch {
    /* nothing to do */
  }
}
